import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { AnimationShell, Link, Typography } from '@/components';
import {
  UserForm,
  UserFormFields,
  useRegisterMutation,
  usePasswordRequestMutation,
} from '@/features/auth';
import { useNotifications } from '@/features/notifications';
import { useRegisterGAPageView } from '@/hooks';
import { Route } from '@/constants';
import { normalizeEmail } from '../helpers';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

export function SignUpPage() {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation(['signup']);
  const { add } = useNotifications();
  useRegisterGAPageView('Sign up');

  const [register] = useRegisterMutation();
  const [isRegistered, setIsRegistered] = useState(false);
  const [isInvited, setIsInvited] = useState(false);
  const [isUnverified, setIsUnverified] = useState(false);
  const [invitedEmail, setInvitedEmail] = useState('');
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [isAlreadyRegistered, setIsAlreadyRegistered] = useState(false);
  const [startTime] = useState(() => Date.now());
  const [mutation] = usePasswordRequestMutation();
  const navigate = useNavigate();

  const handlePasswordRecovery = useCallback(
    (email: string) => {
      mutation({ email })
        .unwrap()
        .then(() => {
          setIsEmailSent(true);
          navigate(Route.RecoveryPasswordSuccess);
        });
    },
    [mutation, navigate]
  );

  const utmParams = {
    origin: searchParams.get('origin') ?? undefined,
    campaign: searchParams.get('campaign') ?? undefined,
  };

  const handleSubmit = useCallback(
    async (data: UserFormFields) => {
      const elapsed = Date.now() - startTime;
      if (elapsed < 2000) {
        // Less than 2 seconds fill time → suspicious
        return;
      }

      if (!data.turnstileToken) {
        add({
          id: new Date().toString(),
          title: t('Verification required'),
          description: t('Please complete the security verification.'),
        });
        return;
      }

      const normalizedEmail = normalizeEmail(data.email);

      const dataToCreateUser = {
        ...data,
        email: normalizedEmail,
        show_modal_ttcc: false,
        ...utmParams,
      };

      register(dataToCreateUser)
        .unwrap()
        .then(() => {
          setIsRegistered(true);
          add({
            id: new Date().toString(),
            title: t('Account created'),
            description: t('Your account has been created'),
          });
        })
        .catch((err) => {
          if (err.status === 409) {
            const errorCode = err.data?.error;
            if (errorCode === 'USER_INVITED') {
              setIsInvited(true);
              setIsRegistered(true);
            } else if (errorCode === 'USER_ALREADY_REGISTERED') {
              setIsAlreadyRegistered(true);
              setIsRegistered(true);
            } else if (errorCode === 'USER_UNVERIFIED') {
              setIsUnverified(true);
              setIsRegistered(true);
            }
            setInvitedEmail(dataToCreateUser.email);
            add({
              id: new Date().toString(),
              title: t('ERROR'),
              description: t('An account already exists under this email.'),
            });
          } else {
            add({
              id: new Date().toString(),
              title: t('ERROR'),
              description: t(`There was an error creating the account. Please try again later.`),
            });
          }
        });
    },
    [startTime, t, add, register, utmParams]
  );

  return (
    <div className="w-full space-y-8">
      {isRegistered ? (
        isInvited ? (
          <>
            <Typography as="h1" size="heading-1" color="primary" weight="semibold" className="text-center">
              {t("You've been invited to your company's account with this address")}
            </Typography>
            {isEmailSent ? (
              <Typography size="body-1" color="primary" className="text-center">
                {t(
                  "Check your email inbox to complete registration. Please check your Spam or Promotions folder if you don't see the email in the next few minutes."
                )}
              </Typography>
            ) : (
              <Button type="submit" size="lg" className="w-full" onClick={() => handlePasswordRecovery(invitedEmail)}>
                {t('Send invitation email')}
              </Button>
            )}
          </>
        ) : isUnverified ? (
          <div className="space-y-4">
            <Typography as="h1" size="heading-1" color="primary" weight="semibold" className="text-center">
              {t('You already have an account but it is not verified yet.')}
            </Typography>
            {isEmailSent ? (
              <Typography size="body-1" color="primary" className="text-center">
                {t('Please check your email inbox to verify your account.')}
              </Typography>
            ) : (
              <Button type="submit" size="lg" className="w-full" onClick={() => handlePasswordRecovery(invitedEmail)}>
                {t('Send email to verify your account')}
              </Button>
            )}
          </div>
        ) : isAlreadyRegistered ? (
          <div className="space-y-4">
            <Typography as="h1" size="heading-1" color="primary" weight="semibold" className="text-center">
              {t('An account already exists under this email')}
            </Typography>
            <Typography size="body-1" color="primary" className="text-center">
              {t('Please check your email account.')}
            </Typography>
            <Typography size="body-1" color="primary" className="text-center">
              {t("If you don't remember your password, click ")}
              <Link to={Route.RecoveryPassword}>{t(`here`)}</Link>
            </Typography>
          </div>
        ) : (
          <div className="space-y-4">
            <Typography as="h1" size="heading-1" color="primary" weight="semibold" className="text-center">
              {t('Check your email inbox to verify your account')}
            </Typography>
            <Typography size="body-1" color="primary" className="text-center">
              {t("Please check your Spam or Promotions folder if you don't see the email in the next few minutes.")}
            </Typography>
          </div>
        )
      ) : (
        <>
          <Typography as="h1" size="heading-1" color="primary" weight="semibold">
            {t(`Create your account`)}
          </Typography>

          <UserForm onSubmit={handleSubmit} />
        </>
      )}

      <AnimationShell type="opacity" delay={0.35} className="w-full">
        <div className="flex items-center justify-center gap-x-1">
          <Typography weight="normal" size="body-2">
            {t(`Already have an account? `)}
          </Typography>
          <Link to={Route.SignIn}>Sign In</Link>
        </div>

        <div className="flex items-center justify-center gap-x-1">
          <Typography weight="normal" size="body-2">
            {t(`Need help? Email us at `)}
          </Typography>
          <Link to="mailto:clients@nteaser.com">clients@nteaser.com</Link>
        </div>
      </AnimationShell>
    </div>
  );
}
